import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const RESERVED_SEGMENTS = new Set([
  '', 'admin', 'login', 'signup', 'setup-account', 'reset-request',
  'verify-device', 'activate', 'onboarding', 'page', 'blog', 'links', 'forum',
  'community',
]);

const CompanyContext = createContext({ company: null, loading: false, error: null, subdomain: null });

export const CompanyProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const subdomain = useMemo(() => {
    const first = pathname.split('/')[1] || '';
    if (RESERVED_SEGMENTS.has(first)) return null;
    return first || null;
  }, [pathname]);

  useEffect(() => {
    if (!subdomain) {
      setCompany(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`/api/public/blog?subdomain=${encodeURIComponent(subdomain)}`, { credentials: 'include' })
      .then(async (res) => {
        if (res.status === 404) throw new Error('Workspace not found');
        if (!res.ok) throw new Error('Could not load workspace');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setCompany(data.blog ? { ...data.blog, isBlocked: !!data.blog.isBlocked } : null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCompany(null);
          setError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [subdomain]);

  return (
    <CompanyContext.Provider value={{ company, loading, error, subdomain }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => useContext(CompanyContext);
